/**
 * 多次散射 LUT（Hillaire 2020 风格 2D 简化版）。
 *
 * 2D 查找表：(x = V·L, y = L·up)，存储地面处从视线方向 V 到达的多次散射辐亮度。
 * 通过一次独立的全屏 GPU pass 预计算；太阳方向/大气系数变化时重算。
 *
 * 渲染流程（SceneManager 每帧驱动）：
 *   render() → 内部 RT（64×32 HalfFloat）→ 主大气着色器按 (V·L, L·up) 采样。
 */

import * as THREE from 'three'
import type { AtmosphereCoeffs } from '../utils/atmosphereParams'
import { SUN_IRRADIANCE } from '../utils/atmosphereParams'
import { fullscreenVertexShader } from './shaders/fullscreen.glsl'
import { multiscatterLUTFragmentShader } from './shaders/multiscatterLUT.glsl'

/** LUT 分辨率（x=视线-太阳夹角，y=太阳天顶角） */
export const MS_LUT_WIDTH = 64
export const MS_LUT_HEIGHT = 32
/** LUT 球面积分采样数（固定） */
export const MS_LUT_SPHERE_SAMPLES = 32
/** LUT 内层 ray march 步数（固定，比主渲染更密以保证平滑） */
export const MS_LUT_MARCH_STEPS = 48

export class MultiScatterLUT {
  readonly texture: THREE.WebGLRenderTarget
  private material: THREE.ShaderMaterial
  private scene = new THREE.Scene()
  private camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
  /** 脏标记：LUT 仅依赖大气系数（与太阳方向/相机无关），系数变化时置脏重算 */
  private dirty = true

  constructor() {
    this.texture = new THREE.WebGLRenderTarget(MS_LUT_WIDTH, MS_LUT_HEIGHT, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
    })

    const uniforms: Record<string, THREE.IUniform> = {
      uResolution: { value: new THREE.Vector2(MS_LUT_WIDTH, MS_LUT_HEIGHT) },
      uTransLUT: { value: null },
      uBetaR: { value: new THREE.Vector3() },
      uBetaM: { value: new THREE.Vector3() },
      uBetaMExt: { value: new THREE.Vector3() },
      uHr: { value: 8 },
      uHm: { value: 1.2 },
      uPlanetR: { value: 6371 },
      uAtmosR: { value: 6471 },
      uG: { value: 0.76 },
      uSunIrradiance: { value: SUN_IRRADIANCE },
      uSampleSteps: { value: MS_LUT_MARCH_STEPS },
      uRayleighOnly: { value: 0 },
      uMieOnly: { value: 0 },
      uNoScatter: { value: 0 },
    }

    this.material = new THREE.ShaderMaterial({
      vertexShader: fullscreenVertexShader,
      fragmentShader: multiscatterLUTFragmentShader,
      uniforms,
      depthTest: false,
      depthWrite: false,
    })

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material)
    quad.frustumCulled = false
    this.scene.add(quad)
  }

  /** 更新大气系数与透射率 LUT */
  setCoeffs(coeffs: AtmosphereCoeffs, lutTexture: THREE.Texture): void {
    const u = this.material.uniforms
    u.uBetaR.value.fromArray(coeffs.betaR)
    u.uBetaM.value.fromArray(coeffs.betaM)
    u.uBetaMExt.value.fromArray(coeffs.betaMExt)
    u.uHr.value = coeffs.Hr
    u.uHm.value = coeffs.Hm
    u.uPlanetR.value = coeffs.R
    u.uAtmosR.value = coeffs.Rt
    u.uG.value = coeffs.g
    u.uTransLUT.value = lutTexture
    this.dirty = true
  }

  /** 渲染 LUT pass 到内部 RT（仅在系数变化后重算，其余帧为 no-op） */
  render(renderer: THREE.WebGLRenderer): void {
    if (!this.dirty) return
    this.dirty = false
    const prev = renderer.getRenderTarget()
    renderer.setRenderTarget(this.texture)
    renderer.render(this.scene, this.camera)
    renderer.setRenderTarget(prev)
  }

  dispose(): void {
    this.texture.dispose()
    this.material.dispose()
  }
}
